import React, { useState, type FunctionComponent } from 'react';
import { injectIntl } from 'react-intl-next';
import debounce from 'debounce-promise';
import { AsyncSelect, components, type OptionProps } from '@atlaskit/select';
import { layers } from '@atlaskit/theme/constants';

import { IconWrapper, Container, Instructions } from './styled';
import { PageIcon, LiveDocIcon, SpaceIcon } from '../../../common/styled';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	type CrossProductSearchSpaceResult,
	searchConfluencePages,
	searchConfluenceSpaces,
} from '../../../../state/epics/requests';
import spacePickerMessages from './messages';
import granularPagesMessages from './messages';
import { type SpacePickerOption, type Props, type OwnProps, type StateProps } from './types';
import { type ConfluenceSearchResultWithLinks } from '../../../../state/confluence/types';
import { AnalyticsEventToProps } from '@atlassian/analytics-bridge';

type PageSearchResult = Pick<ConfluenceSearchResultWithLinks, 'results' | '_links'>;

const Option: FunctionComponent<OptionProps<SpacePickerOption>> = ({ children, ...props }) => (
	//@ts-ignore react-select
	<components.Option {...props}>
		<IconWrapper>
			{props.value?.isPage ? (
				props.value?.isLiveDoc && fg('confluence_live_pages_enabled_in_jira') ? (
					<LiveDocIcon />
				) : (
					<PageIcon />
				)
			) : (
				<SpaceIcon iconPath={props.value?.iconPath} />
			)}
		</IconWrapper>
		{children}
	</components.Option>
);

const mapPageAndSpaceOptions = (
	spaceSearch: CrossProductSearchSpaceResult,
	pageSearch: PageSearchResult,
) => {
	const spaceOptions = spaceSearch?.scopes?.[0]?.results?.map(
		({ title, space, baseUrl, url, id }) => ({
			label: title,
			value: {
				spaceId: Number(id),
				spaceKey: space?.key,
				spaceName: title,
				spaceUrl: `${baseUrl}${url}`,
				iconPath: `${baseUrl}${space?.icon?.path}`,
				isPage: false,
				isLiveDoc: false,
			},
		}),
	);

	const pageOptions = pageSearch?.results?.map(({ title, space, subType, id, _links }) => ({
		label: title,
		value: {
			spaceId: space.id,
			spaceKey: space.key,
			spaceName: space.name,
			linkedPageId: id,
			linkedPageTitle: title,
			isPage: true,
			isLiveDoc: subType === 'live',
			linkedPageUrl: `${pageSearch._links?.base}${_links?.webui}`,
		},
	}));

	return [
		{
			label: 'Spaces',
			options: spaceOptions,
		},
		{
			label: 'Items',
			options: pageOptions,
		},
	];
};

const SPACES_MAX_RESULTS = 5;

const SpacePicker = ({
	intl: { formatMessage },
	onSelected,
	isDisconnectedTemplatesClick,
	isConnectingSpace,
	cloudId,
	projectId,
	projectType,
}: Props) => {
	const [defaultOptions, setDefaultOptions] = useState<Array<any> | boolean>(true);
	const [defaultOptionsAreLoaded, setDefaultOptionsAreLoaded] = useState<boolean>(false);

	const fetchPageAndSpaceOptions = async (inputValue: string) => {
		const pageSearch: Pick<ConfluenceSearchResultWithLinks, 'results' | '_links'> =
			await searchConfluencePages(inputValue, projectId, projectType);
		let spaceSearch: CrossProductSearchSpaceResult = await searchConfluenceSpaces({
			cloudId,
			limit: SPACES_MAX_RESULTS, // Limit displayed spaces to give room for pages results
			searchString: inputValue,
		});
		const options = mapPageAndSpaceOptions(spaceSearch, pageSearch);

		if (!defaultOptionsAreLoaded && !inputValue) {
			// Save initial results to return them when no input is passed
			setDefaultOptionsAreLoaded(true);
			setDefaultOptions(options);
		}

		return options;
	};

	const debouncedOptions = debounce(fetchPageAndSpaceOptions, 300);

	return (
		<Container data-test-id="confluence-project-pages.post-expand.connect-or-update-space-modal.space-picker">
			<Instructions>
				{isDisconnectedTemplatesClick
					? formatMessage(spacePickerMessages['disconnected-templates-description'])
					: formatMessage(granularPagesMessages.description)}
			</Instructions>
			<AsyncSelect<SpacePickerOption>
				// When AsyncSelect defaultOptions prop is set to `false`, `loadOptions` is called on render to populate defaultOptions.
				defaultOptions={defaultOptions}
				loadOptions={debouncedOptions}
				isSearchable
				placeholder={formatMessage(granularPagesMessages.placeholder)}
				isDisabled={isConnectingSpace}
				menuPortalTarget={document.body}
				label={formatMessage(granularPagesMessages.searchLabel)}
				styles={{
					menuPortal: (base) => ({ ...base, zIndex: layers.modal() }),
					option: (base) => ({
						...base,
						display: 'flex',
						alignItems: 'center',
						cursor: 'pointer',
					}),
				}}
				components={{ Option }}
				onChange={(result, { action }) => {
					if (result && action === 'select-option') {
						const { value } = result;
						onSelected(value);
					}
				}}
			/>
		</Container>
	);
};

export default AnalyticsEventToProps('spacePicker', {
	onSelected: 'selected',
	// @ts-ignore - the AnalyticsEventToProps HOC doesn't allow functional components in its typing but there is no reason for that
})(injectIntl(SpacePicker)) as React.FC<OwnProps & StateProps>;
